/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import Fab from '@material-ui/core/Fab';
import MenuOpenIcon from '@material-ui/icons/MenuOpen';
import Drawer from '@material-ui/core/Drawer';
import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isFeatureEnabled, ToggleableFeature } from 'shared/utils/featuresUtils';
import { useSelector } from 'react-redux';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { ScrollSection } from './ScrollSections';
import TopNavigation from '../../layout/menu/TopNavigation';
import Button from '../common/Button';
import useProcedure from '../../procedure/useProcedure';
import { selectAttachmentsAreLoading } from '../../redux/features/submission/submission.selectors';

const useStylesDrawer = makeStyles(theme => ({
  paper: {
    zIndex: '1300',
    width: '100%',
    backgroundColor: theme.palette.sideMenuBackground,
    color: theme.palette.contrastText,
    [theme.breakpoints.up('md')]: {
      width: 'calc(100% / 2)',
    },
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100% / 3)',
    },
    [theme.breakpoints.up('xl2')]: {
      width: 'calc(100% / 4)',
    },
  },
}));

const useStyles = makeStyles(theme => ({
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1.5rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: '2rem',
    },
  },
  fab: {
    backgroundColor: theme.palette[theme.fabColor].main,
    color: theme.palette[theme.fabColor].contrastText,
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },
    '&:focus, &:active': {
      backgroundColor: theme.palette[theme.fabColor].active,
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette[theme.fabColor].hover,
    },
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '2rem',
    padding: '1.5rem 0',
  },
  scrollSection: ({ scrollUp }) => ({
    paddingTop: (scrollUp || scrollUp === 0) && `${110 - scrollUp}px`,
  }),
}));

export default function DraftDrawer({
  user,
  currentOrganization,
  sectionValidations,
  isDrawerOpen,
  toggleDrawer,
  readOnly,
  uiSchema,
  submission,
}) {
  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const configurableHeaderEnabled = isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.HEADER);

  const [scrollUp, setScrollUp] = useState(configurableHeaderEnabled ? 0 : null);

  const shouldScrollUp = () => {
    if (window.scrollY >= 0 && window.scrollY <= 90) {
      setScrollUp(window.scrollY);
    }
  };
  useEffect(() => {
    if (configurableHeaderEnabled) {
      window.addEventListener('scroll', shouldScrollUp);
    }
    return () => {
      if (configurableHeaderEnabled) {
        window.removeEventListener('scroll', shouldScrollUp);
      }
    };
  }, []);

  const classes = useStyles({ scrollUp });
  const classesDrawer = useStylesDrawer({ scrollUp });

  const history = useHistory();
  const { submissionId } = useParams();

  const { t } = useTranslation();
  const procedure = useProcedure();

  const isSmall = useMediaQuery(theme => theme.breakpoints.only('sm'));
  const isLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const attachmentsAreLoading = useSelector(selectAttachmentsAreLoading);

  const previewDisabled = !submissionId || attachmentsAreLoading;

  const handlePreviewClick = useCallback(() => {
    if (Array.isArray(history?.location?.state)) {
      history.push(
        `/${procedure.id}/preview/${submissionId}`,
        [
          ...history?.location?.state,
          {
            goBackText: t('submission.draft'),
            goBackPath: `/${procedure.id}/draft/${submissionId}/edit`,
          },
        ].filter(Boolean),
      );

      return;
    }

    history.push(
      `/${procedure.id}/preview/${submissionId}`,
      [
        history?.location?.state,
        {
          goBackText: t('submission.draft'),
          goBackPath: `/${procedure.id}/draft/${submissionId}/edit`,
        },
      ].filter(Boolean),
    );
  }, [submissionId]);

  return (
    <Drawer
      classes={classesDrawer}
      anchor="right"
      open={isDrawerOpen}
      variant={isLargeOrBigger ? 'permanent' : 'temporary'}
      onClose={toggleDrawer}
    >
      {!isLargeOrBigger ? (
        <div className={classes.buttonsContainer}>
          <Fab size="medium" className={classes.fab} onClick={toggleDrawer}>
            <MenuOpenIcon />
          </Fab>
          {isSmall && (
            // Setting a new custom variant as the requirement was to treat this button as an exception
            <Button
              disabled={previewDisabled}
              color="primary"
              variant="sidebar"
              className={classes.button}
              onClick={handlePreviewClick}
            >
              {t('submission.preview')}
            </Button>
          )}
        </div>
      ) : (
        <div className={classes.actionsWrapper}>
          <TopNavigation user={user} currentOrganization={currentOrganization} />
        </div>
      )}

      <ScrollSection
        previewDisabled={previewDisabled}
        className={classes.scrollSection}
        uiSchema={uiSchema}
        handlePreviewClick={handlePreviewClick}
        progress={!readOnly && sectionValidations}
        submission={submission}
      />
    </Drawer>
  );
}
